import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { Box, Button, Typography } from '@material-ui/core'

import useAuth from 'hooks/useAuth'
import usePermissions from 'hooks/usePermissions'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'

import { CommentItem, CommentForm } from './components'
import useStyles from './styles'

const Comments = ({
  taskId,
  initialComments,
  initialCount,
  disabled = false,
}) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { user } = useAuth()
  const permissions = usePermissions()

  const canDestroyComment = permissions.permitted(
    constants.permissions.KANBAN_TASKS.DESTROY_COMMENT,
  )

  const COMMENTS_PER_PAGE = 10

  const [page, setPage] = useState(1)
  const [newText, setNewText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [comments, setComments] = useState(initialComments || [])
  const [apiCount, setApiCount] = useState(
    initialCount ?? initialComments?.length ?? 0,
  )

  useEffect(() => {
    if (initialComments) {
      setComments(initialComments)
      setApiCount(initialCount ?? initialComments.length)
      setPage(1)
    }
  }, [initialComments, initialCount])

  const handleLoadMore = async () => {
    if (!taskId || loadingMore) return
    setLoadingMore(true)
    try {
      const nextPage = page + 1
      const res = await service.dponet.activityComments.get({
        taskId,
        page: nextPage,
        perPage: COMMENTS_PER_PAGE,
      })
      const newComments = res?.data?.taskComments || []
      const total = res?.data?.meta?.totalCount ?? apiCount
      setComments((prev) => [...prev, ...newComments])
      setApiCount(total)
      setPage(nextPage)
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoadingMore(false)
    }
  }

  const handleAdd = async () => {
    const text = newText.trim()
    if (!text) return
    setSubmitting(true)
    try {
      const res = await service.dponet.activityComments.create({
        taskId,
        comment: text,
      })
      setComments((prev) => [...prev, res.data.taskComment])
      setApiCount((prev) => prev + 1)
      setNewText('')
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = async (commentId, text, isReply, parentId) => {
    try {
      const res = await service.dponet.activityComments.update({
        taskId,
        commentId,
        comment: text,
      })
      const updated = res.data.taskComment
      if (isReply) {
        setComments((prev) =>
          prev.map((c) =>
            c.id === parentId
              ? {
                  ...c,
                  replies: c.replies.map((r) =>
                    r.id === commentId ? updated : r,
                  ),
                }
              : c,
          ),
        )
      } else {
        setComments((prev) =>
          prev.map((c) =>
            c.id === commentId ? { ...updated, replies: c.replies } : c,
          ),
        )
      }
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    }
  }

  const handleDelete = async (id, isReply, parentId) => {
    try {
      await service.dponet.activityComments.destroy({ taskId, commentId: id })
      if (isReply) {
        setComments((prev) =>
          prev.map((c) =>
            c.id === parentId
              ? { ...c, replies: c.replies.filter((r) => r.id !== id) }
              : c,
          ),
        )
      } else {
        setComments((prev) => prev.filter((c) => c.id !== id))
        setApiCount((prev) => Math.max(0, prev - 1))
      }
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    }
  }

  const handleAddReply = async (parentId, text) => {
    try {
      const res = await service.dponet.activityComments.create({
        taskId,
        comment: text,
        parentId,
      })
      setComments((prev) =>
        prev.map((c) =>
          c.id === parentId
            ? { ...c, replies: [...(c.replies || []), res.data.taskComment] }
            : c,
        ),
      )
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    }
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={12}>
      {comments.length === 0 && (
        <Typography variant="body2" className={classes.emptyText}>
          Nenhum comentário ainda.
        </Typography>
      )}

      {comments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          currentUser={user}
          canDestroyComment={canDestroyComment}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onAddReply={handleAddReply}
          unlinked={disabled}
        />
      ))}

      {apiCount > comments.length && (
        <Button size="small" onClick={handleLoadMore} disabled={loadingMore}>
          {loadingMore
            ? 'Carregando...'
            : `Exibir mais comentários (${apiCount - comments.length})`}
        </Button>
      )}

      {taskId && !disabled && (
        <CommentForm
          value={newText}
          onChange={setNewText}
          onSubmit={handleAdd}
          disabled={submitting}
        />
      )}
    </Box>
  )
}

Comments.propTypes = {
  taskId: PropTypes.number,
  initialComments: PropTypes.array,
  initialCount: PropTypes.number,
  disabled: PropTypes.bool,
}

export default Comments
